import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, selectAuth } from '../features/authSlice';
import { useBranding } from '../hooks/useBranding';

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, error } = useSelector(selectAuth);
  const { branding } = useBranding();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim() && password) {
      dispatch(loginUser({ email: email.trim(), password }));
    }
  };

  const primaryColor = branding?.primary_color || '#3b86f5';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <style>{`
        @keyframes loginFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-card-enter { animation: loginFadeIn 0.5s ease-out forwards; }
      `}</style>

      {/* Background: mesh gradient + subtle grid */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59, 130, 246, 0.15), transparent),
            radial-gradient(ellipse 60% 40% at 100% 50%, rgba(99, 102, 241, 0.08), transparent),
            radial-gradient(ellipse 60% 40% at 0% 80%, rgba(59, 130, 246, 0.06), transparent),
            linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)
          `,
        }}
      />
      <div
        className="absolute inset-0 -z-10 opacity-[0.4]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2394a3b8' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="w-full max-w-[400px] login-card-enter">
        <div className="mb-10">
          <div className="flex justify-center mb-5">
            <img
              src={branding?.logo || '/skillform-logo-dark.png'}
              alt={branding?.name || 'Skillform'}
              className="max-w-[160px] h-auto object-contain drop-shadow-sm"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 text-center tracking-tight">Exam Simulator</h1>
          <p className="text-slate-500 mt-2 text-center text-[15px]">Sign in to continue your learning</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/80 p-8 space-y-5"
        >
          {error && (
            <div className="text-sm text-red-700 bg-red-50/90 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <span className="flex-shrink-0 w-1 h-4 bg-red-500 rounded-full" />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <MailIcon />
              </div>
              <input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder:text-slate-400 text-[15px] focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition-all duration-200 hover:border-slate-300"
                style={{ '--tw-ring-color': primaryColor }}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <LockIcon />
              </div>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder:text-slate-400 text-[15px] focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition-all duration-200 hover:border-slate-300"
                style={{ '--tw-ring-color': primaryColor }}
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div className="pt-1">
            <button
              type="submit"
              disabled={isLoading || !email.trim() || !password}
              className="w-full py-3.5 rounded-xl font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.99] flex items-center justify-center gap-2"
              style={{ background: primaryColor }}
            >
              {isLoading ? (
                <>
                  <SpinnerIcon />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        </form>

        <p className="text-center mt-5">
          <a
            href="/forgot-password"
            className="text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors inline-flex items-center gap-1"
          >
            Forgot password?
          </a>
        </p>

        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="text-slate-400 text-xs">Powered by</span>
          <img
            src="/skillform-logo-dark.png"
            alt="Skillform"
            className="h-4 object-contain opacity-50"
          />
        </div>
      </div>
    </div>
  );
}
